import React from "react"
import { library } from "@fortawesome/fontawesome-svg-core"
import { fas }     from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
library.add(fas)

import { QuoteEditorProvider, useQuoteEditor } from "./context/QuoteEditorContext"
import { EndpointsProvider }   from "./context/EndpointsContext"
import { GroupPickerProvider } from "./context/GroupPickerContext"
import MetaCard       from "./components/MetaCard"
import TreeCard       from "./components/TreeCard"
import SummaryPane    from "./components/SummaryPane"
import BuilderFooter  from "./components/BuilderFooter"
import AddGroupModal  from "./components/AddGroupModal"
import TabBar         from "./components/TabBar"
import SettingsTab    from "./components/SettingsTab"
import MarginsTab     from "./components/MarginsTab"
import LibraryTab     from "./components/LibraryTab"
import HistoryButtons from "./components/HistoryButtons"
import DensityToggle  from "./components/DensityToggle"
import usePersistedFlag from "./hooks/usePersistedFlag"
import useHistoryShortcuts from "./hooks/useHistoryShortcuts"

// The summary takes 380 px off the "Description" column, so it is closed by
// default on a narrow screen until the user decides otherwise.
const SUMMARY_NEEDS = 1500

const fitsSummary = () => {
  try {
    return window.innerWidth >= SUMMARY_NEEDS
  } catch (_error) {
    return true
  }
}

const Layout = ({ backRoute, saveUrl, saveMethod }) => {
  const { state, dispatch } = useQuoteEditor()
  useHistoryShortcuts(dispatch)
  // The folding of the two frames follows the user from session to session.
  const [summaryOpen, toggleSummary] = usePersistedFlag("quoteBuilder.summaryOpen", fitsSummary())
  const [metaOpen,    toggleMeta]    = usePersistedFlag("quoteBuilder.metaOpen",    true)
  const [compact,     toggleCompact] = usePersistedFlag("quoteBuilder.compact",     false)
  // A missing flag must close the margins, never open them.
  const showMargin = !!state.showMargin
  const requested  = state.activeTab || "edit"
  const readOnly   = !!state.readOnly
  const readOnlyNotice = state.readOnlyReason === "project_closed"
    ? I18n.t("quotes.builder.read_only_project_closed_notice")
    : state.readOnlyReason === "avenant_signed"
    ? I18n.t("quotes.builder.read_only_avenant_notice")
    : I18n.t("quotes.builder.read_only_notice")
  const project    = state.project
  const fallback   = readOnly ? "preview" : "edit"
  const tab = requested === "margins" && !showMargin ? fallback
            : (readOnly && requested === "edit")     ? "preview"
            : requested
  const isSheet = !["settings", "margins", "library"].includes(tab)

  return (
    <div
      data-mode    = {tab}
      data-readonly = {readOnly ? "1" : "0"}
      data-margins = {showMargin ? "visible" : "hidden"}
      data-density = {compact ? "compact" : "comfortable"}
      className = {`px-7 pt-4 ${isSheet ? "pb-4 h-[calc(100vh-130px)] flex flex-col overflow-hidden" : "pb-10 min-h-full"}`}
    >
      <div className="flex items-center gap-3 mb-4 shrink-0">
        {/* Back-to-list arrow — same square pattern as the chantier panorama */}
        <a
          href      = {backRoute || "/quotes"}
          title     = "Retour à la liste"
          className = "shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900"
        >
          <FontAwesomeIcon icon="arrow-left" className="text-[13px]" />
        </a>

        {/* Un devis de chantier se lit depuis le chantier et y retourne. La
            flèche ramène là d'où l'on vient, qui n'est pas toujours le
            chantier : ce bouton, lui, le dit et y mène toujours. Il est libellé
            plutôt que muet, pour que l'aller-retour ne se devine pas. */}
        {project?.path && !readOnly && (
          <a
            href      = {project.path}
            title     = {project.avenant ? `${project.name} · ${project.avenant.number}` : project.name}
            className = "shrink-0 inline-flex items-center gap-2 h-9 px-3 rounded-md border border-slate-300 bg-white text-[12.5px] font-semibold text-slate-700 hover:bg-slate-50 hover:text-slate-900 hover:border-slate-400"
          >
            <FontAwesomeIcon icon="helmet-safety" className="text-[12px] text-slate-400" />
            {I18n.t("quotes.builder.go_to_project")}
            {project.avenant && (
              <span className="font-mono text-[11px] font-normal text-slate-400">{project.avenant.number}</span>
            )}
          </a>
        )}

        <TabBar />
        <HistoryButtons />
        {isSheet && (
          <div className="flex items-center pl-1 ml-1 border-l border-slate-200">
            <DensityToggle compact={compact} onToggle={toggleCompact} />
          </div>
        )}
        <span className="flex-1" />
        {/* A read-only quote belongs to its chantier: the notice that says why
            it is locked is also the way there, so one button carries both. */}
        {readOnly && project?.path && (
          <a
            href      = {project.path}
            title     = {readOnlyNotice}
            className = "shrink-0 whitespace-nowrap inline-flex items-center gap-2 h-9 px-3 rounded-md border border-slate-300 bg-white text-[12.5px] font-semibold text-slate-700 hover:bg-slate-50 hover:text-slate-900 hover:border-slate-400"
          >
            <FontAwesomeIcon icon="lock" className="text-[11px] text-slate-400" />
            {state.readOnlyReason === "project_closed"
              ? I18n.t("quotes.builder.read_only_project_closed_link")
              : state.readOnlyReason === "avenant_signed"
              ? I18n.t("quotes.builder.read_only_avenant_link")
              : I18n.t("quotes.builder.read_only_project_link")}
            {project.avenant && (
              <span className="font-mono text-[11px] font-normal text-slate-400">{project.avenant.number}</span>
            )}
            <FontAwesomeIcon icon="arrow-right" className="text-[11px] text-slate-400" />
          </a>
        )}
        {readOnly && !project?.path && (
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-slate-100 border border-slate-200 text-[12.5px] font-medium text-slate-600">
            <FontAwesomeIcon icon="lock" className="text-[11px]" />
            {readOnlyNotice}
          </span>
        )}
        <BuilderFooter backRoute={backRoute} saveUrl={saveUrl} saveMethod={saveMethod} hideBackArrow={true} readOnly={readOnly} />
      </div>

      {/* Consultation is enforced on the server, where update? is refused. The
          screen says so with `inert`, placed on what is read rather than on the
          frame around it: an inert scroll container stops scrolling, and a quote
          you cannot scroll is not a quote you can consult. */}
      {tab === "settings" ? (
        <SettingsTab />
      ) : tab === "library" ? (
        <LibraryTab />
      ) : tab === "margins" && showMargin ? (
        <MarginsTab />
      ) : (
        <div
          className={`grid gap-4 flex-1 min-h-0 grid-rows-[minmax(0,1fr)] ${summaryOpen ? "grid-cols-[1fr_380px]" : "grid-cols-1"}`}
        >
          <main className="min-w-0 flex flex-col min-h-0">
            <div {...(readOnly ? { inert: "" } : {})}>
              <MetaCard
                metaOpen        = {metaOpen}
                onToggleMeta    = {toggleMeta}
                summaryOpen     = {summaryOpen}
                onToggleSummary = {toggleSummary}
              />
            </div>
            <TreeCard />
          </main>
          {summaryOpen && (
            <aside className="bg-white border border-slate-200 rounded-2xl flex flex-col min-h-0 overflow-y-auto">
              {/* The pane scrolls on its own, so the inertness goes inside it. */}
              <div {...(readOnly ? { inert: "" } : {})}>
                <SummaryPane />
              </div>
            </aside>
          )}
        </div>
      )}
    </div>
  )
}

const App = ({ data, back_route, save_url, save_method, endpoints, current_user_id }) => (
  <QuoteEditorProvider data={data}>
    <EndpointsProvider value={{ ...(endpoints || {}), current_user_id }}>
      <GroupPickerProvider>
        <Layout backRoute={back_route} saveUrl={save_url} saveMethod={save_method} />
        <AddGroupModal />
      </GroupPickerProvider>
    </EndpointsProvider>
  </QuoteEditorProvider>
)

export default App
